<template lang="pug">
  .page-main-wat(:class='sessionGetDisplayClass(["body", "content"])')

    //- Back to top button
    a.btn.btn-submit.show-tooltips-button.mr-3#show-tooltips(@click='showTooltips = !showTooltips').no-print
      font-awesome-icon(:icon='["fas", "info-circle"]')

    //- Audit history modal
    .modal.fade#modalAudit(v-if='canSeeAudit', :class='sessionGetDisplayClass(["body", "content"])', tabindex='-1', role='dialog', aria-labelledby='modalAuditLabel', aria-hidden='true')
      .modal-dialog.modal-dialog-scrollable.modal-wide(role="document")
        .modal-content
          .modal-header
            h5.modal-title#modalAuditLabel Change History
            button.close(type='button', data-dismiss='modal', aria-label='Close')
              span(aria-hidden='true') &times;

          .modal-body
            p The table below shows the change history for this particular WAT record.  Changes between versions are highlighted.
            p
              small NOTE: Only fields that has had a change will show up here; check the current record to see all current values.  Freeform text changes will come in an upcoming update.
            table.table.table-striped.smaller-text(v-if='facultyData.fullAudit && facultyData.fullAudit.length > 0')
              thead
                th(v-for='auditCol in auditColumns') {{ auditCol.label }}
              tbody
                tr(v-for='auditEntry in facultyData.fullAudit')
                  td(v-for='auditCol in auditColumns', :class='includes(auditEntry.changed, auditCol.value) ? "bg-info text-white" : ""') {{ auditEntry[auditCol.value] }}
            p(v-else) There is no change history available, this is the original record. 😎

          .modal-footer
            button.btn.btn-secondary(type='button', data-dismiss='modal') Close

    //- Saving confirmation modal
    .modal.fade#modalSaveConfirm(:class='sessionGetDisplayClass(["body", "content"])', tabindex='-1', role='dialog', aria-labelledby='modalTemplateLabel', aria-hidden='true')
      .modal-dialog.modal-lg(role="document")
        .modal-content
          .modal-header
            h5.modal-title#modalTemplateLabel Updating Record Status to: {{ currentRecordStatusLabel }}
            button.close(type='button', data-dismiss='modal', aria-label='Close')
              span(aria-hidden='true') &times;

          .modal-body
            .row.mb-3
              .col(v-if='targetIsForReview || targetIsFinal') {{ templateHelpText }}

            .row.mb-3
              .col(v-if='canFinalizeWat && !targetIsFinal') You will still be able to edit this record afterwards.
              .col(v-else) You will NOT be able to edit this record afterwards.

            .row
              .col-6.text-center
                button.btn.btn-outline-primary(type='button', data-dismiss='modal', @click='doSaveAsDraft') No, save as Draft instead
              .col-6.text-center
                button.btn.btn-success(type='button', data-dismiss='modal', @click='doSave(currentRecordStatus)') Yes, save as {{ currentRecordStatusLabel }}

          .modal-footer
              button.btn.btn-outline-danger(type='button', data-dismiss='modal') Cancel

    .container-fluid.text-center(v-if='isLoading')
      Loading(:showMessages='true')

    .container-fluid.text-danger.text-center(v-if='!isLoading && showErrorMessage') {{ showErrorMessage }}

    .container.px-0(v-show='!isLoading && !showErrorMessage', :class='sessionGetDisplayClass(["content", "bg"])', ref='pageBody')

      .sticky-header.pb-2#the-sticky-header(:class='sessionGetDisplayClass("sticky-header")', :style='sessionWat.bannerMessage.show ? { "padding-top": "140px" } : {}')
        .row.align-items-center.py-1
          .col-auto
            router-link(v-if='fromHome', :to='getRouterLink("home")', @click.native='scrollToTop')
              font-awesome-icon.mr-2(:icon='["fas", "chevron-left"]')
              | Home
            router-link.link(v-else, :to='getRouterLink("templates")', @click.native='scrollToTop')
              font-awesome-icon.mr-2(:icon='["fas", "chevron-left"]')
              | All Faculty

          .col-auto.pl-2.pr-0.d-none.small-header-name {{ facultyData.EMPLOYEE_NAME }}
          .col
          .col-auto.pl-2.pr-4.col-auto.d-none.small-header-name(v-if='workloadStatusIcon')
            font-awesome-icon(:class='workloadStatusClass', :icon='workloadStatusIcon', :title='hasMetWorkloadStatus')
            span.pl-1(:class='workloadStatusClass') {{creditsLabelForSticky}}
          //- .col-auto.pl-2.mb-1.d-none.small-header-name
          //-   span.badge(:class='getBadgeClass') {{ badgeLabel }}

          .col-auto.px-0.d-none.small-header-name
            select.custom-select#template-status-bar(v-model='currentRecordStatus', :disabled='!canEditWat', @change='makeDirty')
              option(v-for='status in filteredRecordStatuses', :value='status.value') {{ status.dropdownLabel }}
          .col-auto.align-self-end.ml-2.pl-0.d-none.small-header-name
            button.btn.btn-primary.full-width(type='button', :disabled='!canSave', @click='doSave(false)')
              font-awesome-icon.mr-1(v-if='isSaving', :icon='["fas", "spinner"]', spin)
              | Save

    .container.px-0(v-show='!isLoading && !showErrorMessage', :class='sessionGetDisplayClass(["content", "bg"])')

      //- Success toasts
      .toast.success-toast(role='alert', aria-live='assertive', aria-atomic='true', data-delay='2500', :style='toastPosition')
        .toast-header.bg-primary.text-light
          strong.mr-auto Save successful
          button.ml-2.mb-1.close(type='button', data-dismiss='toast', aria-label='Close')
            span.text-light(aria-hidden='true') &times;
        .toast-body {{ facultyData.EMPLOYEE_NAME }} has been saved.

      //- Error toasts
      .toast.error-toast(role='alert', aria-live='assertive', aria-atomic='true', data-delay='2500', :style='toastPosition')
        .toast-header.bg-danger.text-light
          strong.mr-auto Failed to save
          button.ml-2.mb-1.close(type='button', data-dismiss='toast', aria-label='Close')
            span.text-light(aria-hidden='true') &times;
        .toast-body {{ saveErrorMessage || "Oops, something went wrong, please try again." }}


      form.mt-1.pb-5.needs-validation.pt-3(@change='makeDirty()')
        .row.mb-1
          .col-auto.pr-0.mr-2.text-info(v-if='getStatusLabel')
            font-awesome-icon.mr-1(:icon="['fas', 'exclamation-triangle']")
            | {{ getStatusLabel }}
          .col.text-right(v-if='workloadStatusIcon')
            font-awesome-icon.mr-1(:class='workloadStatusClass', :icon='workloadStatusIcon', :title='hasMetWorkloadStatus')
            span(:class='workloadStatusClass') {{creditsLabel}} [{{ hasMetWorkloadStatus }}]

        .row.mb-1(v-if='!this.facultyDataIsValid')
          .col.text-danger.text-right.small
            | There are errors on the template that need to be corrected before saving.

        //- start the info header
        .row.align-items-center.mb-1
          .col-auto.header-left-box.font-weight-bold Name:
          .col {{ facultyData.EMPLOYEE_NAME }}

          .col-auto.pr-0.mr-2 Review Status:
          .col-auto.px-0
            select.custom-select#template-status(v-model='currentRecordStatus', :disabled='!canEditWat', @change='makeDirty')
              option(v-for='status in filteredRecordStatuses', :value='status.value') {{ status.dropdownLabel }}
          .col-auto.align-self-end.ml-2.pl-0
            button.btn.btn-primary.full-width(type='button', :disabled='!canSave', @click='doSave(false)')
              font-awesome-icon.mr-1(v-if='isSaving', :icon='["fas", "spinner"]', spin)
              | Save

        .row.align-items-center.mb-1
          .col-auto.header-left-box.font-weight-bold Department:
          .col {{ headerDepartmentTitle }}
          .col-auto.pr-0.mr-2 Job Type:
          .col-auto.pl-0.header-right-box
            select.custom-select#jobType(v-if='canEditWat', v-model='facultyData.JOB_TYPE', @change='onJobTypeChange(facultyData)')
              option(v-for='jobType in jobTypes', :value='jobType.value') {{ jobType.label }}
            .font-weight-bold(v-else) {{ facultyData.JOB_TYPE }}

        .row.align-items-center.mb-1
          .col-auto.header-left-box.font-weight-bold Title:
          .col {{ facultyData.JOB_TITLE }}, {{ facultyData.TENURE_TYPE }}
          .col-auto.pr-0.mr-2 Full-Time Equivalent:
          .col-auto.pl-0.header-right-box
            input.form-control#facultyDepartmentFTE(v-if='canEditWat', type='number', v-model='facultyData.FTE', :class='getNegativeAndRequiredValidation(facultyData.FTE)', min='0', @change='roundTopLevelValue("FTE")')
            .font-weight-bold(v-else) {{ facultyData.FTE }}
            .invalid-feedback FTE must be greater than 0

        .row.align-items-center
          template(v-if='multipleAppt')
            .col-auto.header-left-box.font-weight-bold 2nd Appointment:
            .col {{ multipleAppt }}
          template(v-else)
            .col-auto
            .col
          .col-auto.pr-0.mr-2 Expected {{ getAcademicYearStart }}-{{ getAcademicYearEnd }} Workload:
          .col-auto.pl-0.header-right-box
            input.form-control#facultyExpectedTE(v-if='canEditWat', type='number', v-model='facultyData.FULL_LOAD_CREDITS', :class='getNegativeAndRequiredValidation(facultyData.FULL_LOAD_CREDITS)', min='0', @change='roundTopLevelValue("FULL_LOAD_CREDITS")')
            .font-weight-bold(v-else) {{ facultyData.FULL_LOAD_CREDITS }}
            .invalid-feedback Expected TE must be greater than 0

        .row.faculty-header.font-weight-bold.mt-3.mb-2.mx-0
          .col.py-1 1. FACULTY EXEMPTION STATUS (Faculty Headcount & Assignment will not be included in the final total)

        .row.mx-0.hoverable-row(v-for='(status, statusIndex) in facultyStatuses', :class='{ "py-2": statusIndex < facultyStatuses.length }')
          .col-auto
            .row
              .col.article.px-5
                .item {{ status.ATTRIBUTE_DESC }}
                .dots
                .price
                  input.form-check-input(type='checkbox', :id='status.ATTRIBUTE_CODE', v-model='status.ATTRIBUTE_VALUE', :disabled='!canEditWat')
            .row(v-if='showTooltips')
              .col.small.text-muted.px-5 {{ getCustomLabel(`${status.ATTRIBUTE_CODE}.tooltip`, status.TOOLTIP) }}


        .row.header-assigned-workload.font-weight-bold.mt-3.mb-2.mx-0
          .col.py-1 2. ASSIGNED WORKLOAD
        .row.mx-0
          .col.pl-3 2a. Teaching
        .row.mx-0.align-items-end.py-1.hoverable-row
          .col-auto
            .row
              .col.article.pl-5.mr-0.pr-0
                .item Classroom Instruction
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", CODE_ACTUAL_CLASSROOM_INSTRUCTON)')
                .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0
              .col-auto.pl-1.pr-5 credits
            .row(v-if='showTooltips')
              .col.small.text-muted.px-5 {{ getCustomLabel(`${CODE_ACTUAL_CLASSROOM_INSTRUCTON}.tooltip`, getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).TOOLTIP) }}

        .row.mx-0.align-items-end.py-1(:class='{ "text-muted": individualShouldBeDisabled, "hoverable-row": !individualShouldBeDisabled }')
          .col-auto
            .row
              .col.article.pl-5.mr-0.pr-0
                .item Individual Instruction
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE', :disabled='individualShouldBeDisabled', :class='getNegativeNumberValidation(getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", CODE_INDIVIDUAL_INSTRUCTION)')
                .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0
              .col-auto.pl-1.pr-5 credits
            .row(v-if='individualShouldBeDisabled')
              .col.px-5.small.text-muted Not applicable for your campus.
            .row(v-else-if='showTooltips')
              .col.small.text-muted.px-5 {{ getCustomLabel(`${CODE_INDIVIDUAL_INSTRUCTION}.tooltip`, getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).TOOLTIP) }}

        .row.mx-0.align-items-end.py-4.hoverable-row
          .col-auto
            .row
              .col.article.pl-3.mr-0.pr-0
                .item 2b. Research
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='researchTeSection.ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(researchTeSection.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", researchTeSection.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ researchTeSection.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0.
              .col-auto.pl-1.pr-5 credits
            .row(v-if='showTooltips')
              .col.small.text-muted.px-5 {{ getCustomLabel(`${researchTeSection.ATTRIBUTE_CODE}.tooltip`, researchTeSection.TOOLTIP) }}

        .row.mx-0
          .col.pl-3 2c. Service (Primarily for Instructional Faculty)
        .row.mx-0.align-items-end.py-2(v-for='serviceType in serviceTeSection', :class='{ "text-muted": teShouldBeDisabled(serviceType.ATTRIBUTE_CODE), "hoverable-row": !teShouldBeDisabled(serviceType.ATTRIBUTE_CODE) }')
          .col-auto
            .row
              .col.article.pl-5.mr-0.pr-0
                .item {{ getCustomLabel(`${serviceType.ATTRIBUTE_CODE}.title`, serviceType.ATTRIBUTE_DESC) }}
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='serviceType.ATTRIBUTE_VALUE', :disabled='teShouldBeDisabled(serviceType.ATTRIBUTE_CODE)', :class='getNegativeNumberValidation(serviceType.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", serviceType.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ serviceType.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0.
              .col-auto.pl-1.pr-5 credits
            .row(v-if='teShouldBeDisabled(serviceType.ATTRIBUTE_CODE)')
              .col.px-5.small.text-muted Not applicable for your campus.
            .row(v-else-if='showTooltips')
              .col.px-5.small.text-muted {{ getCustomLabel(`${serviceType.ATTRIBUTE_CODE}.tooltip`, serviceType.TOOLTIP) }}

        .row.mx-0.align-items-end.py-4(:class='{ "text-muted": regularDutiesShouldBeDisabled, "hoverable-row": !regularDutiesShouldBeDisabled }')
          .col-auto
            .row
              .col.article.pl-3.mr-0.pr-0
                .item 2d. Assigned Professional Duties
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='regularDutiesTeSection.ATTRIBUTE_VALUE', :disabled='regularDutiesShouldBeDisabled' :class='getNegativeNumberValidation(regularDutiesTeSection.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", regularDutiesTeSection.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ regularDutiesTeSection.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0
              .col-auto.pl-1.pr-5 credits
            .row(v-if='regularDutiesShouldBeDisabled')
              .col.px-5.small.text-muted Not applicable for your job type.
            .row(v-else-if='showTooltips')
              .col.small.text-muted.px-5 {{ getCustomLabel(`${regularDutiesTeSection.ATTRIBUTE_CODE}.tooltip`, regularDutiesTeSection.TOOLTIP) }}

        .row.mx-0.align-items-end.py-3(:class='{ "text-muted": releaseShouldBeDisabled, "hoverable-row": !releaseShouldBeDisabled }')
          .col-auto
            .row
              .col.article.pl-3.mr-0.pr-0
                .item 2e. Release
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='releaseTeSection.ATTRIBUTE_VALUE', :disabled='releaseShouldBeDisabled', :class='getNegativeNumberValidation(releaseTeSection.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", releaseTeSection.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ releaseTeSection.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0.
              .col-auto.pl-1.pr-5 credits
            .row(v-if='releaseShouldBeDisabled')
              .col.px-5.small.text-muted Not applicable for your campus.
            .row(v-else-if='showTooltips')
              .col.px-5.small.text-muted {{ getCustomLabel(`${releaseTeSection.ATTRIBUTE_CODE}.tooltip`, releaseTeSection.TOOLTIP) }}

        .row.mx-0.align-items-end.py-3(:class='{ "text-muted": buyoutShouldBeDisabled, "hoverable-row": !buyoutShouldBeDisabled }')
          .col-auto
            .row
              .col.article.pl-3.mr-0.pr-0
                .item 2f. Buyout
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='buyoutTeSection.ATTRIBUTE_VALUE', :disabled='buyoutShouldBeDisabled', :class='getNegativeNumberValidation(buyoutTeSection.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", buyoutTeSection.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ buyoutTeSection.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0.
              .col-auto.pl-1.pr-5 credits
            .row(v-if='buyoutShouldBeDisabled')
              .col.px-5.small.text-muted Not applicable for your campus.
            .row(v-else-if='showTooltips')
              .col.px-5.small.text-muted {{ getCustomLabel(`${buyoutTeSection.ATTRIBUTE_CODE}.tooltip`, buyoutTeSection.TOOLTIP) }}

        .row.mx-0.align-items-end.py-3.hoverable-row
          .col-auto
            .row
              .col.article.pl-3.mr-0.pr-0
                .item 2g. Sabbatical (Fall/Spring)
                .dots
              .col-auto.price.px-0.mx-0
                input.form-control.shorter-input(v-if='canEditWat', type='number', v-model='sabbaticalTeSection.ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(sabbaticalTeSection.ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", sabbaticalTeSection.ATTRIBUTE_CODE)')
                .font-weight-bold(v-else) {{ sabbaticalTeSection.ATTRIBUTE_VALUE }}
                .invalid-feedback Credits must be greater than 0.
              .col-auto.pl-1.pr-5 credits
            .row(v-if='showTooltips')
              .col.px-5.small.text-muted {{ getCustomLabel(`${sabbaticalTeSection.ATTRIBUTE_CODE}.tooltip`, sabbaticalTeSection.TOOLTIP) }}

        .row.mx-0.align-items-center.py-3
          .col.font-weight-bold.text-right Total credits:
          .col-auto.price.px-0.mx-0 {{ actualLoad }}
          .col-auto.pl-1.pr-5 credits

        .row.mx-0.align-items-end
          .col.pr-5
            label.mb-0(for='commentsForm') 2h. Comments
            .row(v-if='showTooltips')
              .col.small.text-muted {{ getCustomLabel('COMMENTS.tooltip', '') }}
            textarea#commentsForm.form-control(v-model='facultyData.COMMENTS', :class='getCommentsClass', rows='3', @change='makeDirty()', :disabled='!canEditWat')
            .invalid-feedback Comments are required if there is a change in TE credits.

        .row.header-assigned-workload.font-weight-bold.mt-3.mb-2.mx-0
          .col.py-1 3. REFERENCE & TEMPLATE HISTORY

        .row.mx-0.mt-2
          .col.pl-3 3a. Class Teaching History
        template(v-if='is4YearCampus')
          template(v-if='classRoomInstructionClasses.length > 0 || individualInstructionClasses.length > 0')
            .row.my-2
              .col.px-5
                table.data-table.table-bordered.table-striped
                  thead
                    th Instruction Type
                    th Term
                    th Course
                    th CRN
                    th XL
                    th Credits
                    th Regs
                    th Work Assignment Credit Hours
                  tbody
                    tr(v-for='classItem in classRoomInstructionClasses')
                      td Classroom
                      td {{ classItem.TERM }}
                      td {{ classItem.COURSE }}
                      td {{ classItem.UNIQUEID }}
                      td {{ classItem.AC_PRIMARY_SEC_IRO }}
                      td {{ classItem.SH_IRO }}
                      td {{ classItem.REGS_FOR_INV }}
                      td {{ classItem.CREDIT_FOR_WORKLOAD }}
                    tr(v-for='classItem in individualInstructionClasses')
                      td Individual
                      td {{ classItem.TERM }}
                      td {{ classItem.COURSE }}
                      td {{ classItem.UNIQUEID }}
                      td {{ classItem.AC_PRIMARY_SEC_IRO }}
                      td {{ classItem.SH_IRO }}
                      td {{ classItem.REGS_FOR_INV }}
                      td -
          template(v-else)
            .px-5.mt-2
              .col There are no classes found to estimate classroom instruction credits.
        template(v-else)
          template(v-if='combinedCCClasses.length > 0')
            .row.my-2
              .col.px-5
                table.data-table.table-bordered.table-striped
                  thead
                    th Instruction Type
                    th Term
                    th Course
                    th CRN
                    th XL
                    th Credits
                    th Regs
                    th Work Assignment Credits
                  tbody
                    tr(v-for='classItem in combinedCCClasses')
                      td Classroom
                      td {{ classItem.TERM }}
                      td {{ classItem.COURSE }}
                      td {{ classItem.UNIQUEID }}
                      td {{ classItem.AC_PRIMARY_SEC_IRO }}
                      td {{ classItem.SH_IRO }}
                      td {{ classItem.REGS_FOR_INV }}
                      td {{ classItem.CREDIT_FOR_WORKLOAD }}
                    template(v-if='getFacultyInstructionalForCode(CODE_OTA).ATTRIBUTE_VALUE')
                      td Non-credit/Other teaching assignment (OTA)
                      td
                      td
                      td
                      td
                      td
                      td
                      td {{ getFacultyInstructionalForCode(CODE_OTA).ATTRIBUTE_VALUE }}
          template(v-else)
            .px-5.mt-2
              .col There are no classes found to estimate classroom instruction credits.
        //
        .row.mx-0.mt-4
          .col.pl-3 3b. Non-Instructional Credits & Other UHCC Instruction (OTA)  (From Banner)
        .row.my-2(v-if="getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_EXPLAIN || this.hasNonInstExplain")
          .col.px-5
            table.data-table.table-bordered.table-striped
              thead
                th Attribute
                th Explain
              tbody
                tr(v-if="getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_EXPLAIN")
                  td Other Instruction
                  td {{ getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_EXPLAIN }}
                tr(v-for='nonInstr in this.facultyNonInstructional'  v-if="nonInstr.ATTRIBUTE_EXPLAIN")
                  td {{ nonInstr.ATTRIBUTE_DESC }}
                  td {{ nonInstr.ATTRIBUTE_EXPLAIN }}
        .row.mx-0.mt-2(v-else)
          .px-5
            .col No Records Found.
        .row.mx-0.mt-4.mb-2
          .col.pl-3 3c. Template Approval History
        template(v-if='facultyData.audit && facultyData.audit.length > 0')
          .row.mt-3
            .col.px-5
              table.data-table.table-bordered.table-striped
                thead
                  th Date
                  th Status
                  th Username
                tbody
                  tr(v-for='auditRecord in facultyData.audit')
                    td {{ auditRecord.AUDIT_DATE }}
                    td {{ auditRecord.RECORD_STATUS }}
                    td {{ (auditRecord.AUDIT_USERNAME || 'IRAPO') }}
        template(v-else)
          .row.mx-0
            .px-5
              .col No Template Approval History found; This is the original record.

        .row.mt-5.mb-4(v-if='canSeeAudit')
          .col.px-5
            button.btn.btn-info(type='button', @click='showAuditHistory') Change History
              font-awesome-icon.ml-2(:icon='["fas", "history"]')
</template>

<script>
import { cloneDeep, concat, differenceWith, each, every, find, filter, first, flatten, get, has, includes, isEmpty, isEqual, map, omit, reduce, sortBy, values } from 'lodash';
import { mapGetters } from 'vuex';
import watResource from '@api/wat/watResource';
import Loading from '@components/Loading';
import watUtil from '@api/wat/watUtil';
import util from '@api/util';
import eventBus from '@api/eventBus';
import assets from '@api/assets';
import { DateTime } from 'luxon';

const CAMPUS_MANOA = 'MAN';
const CAMPUS_HILO = 'HIL';

const _ = { cloneDeep, concat, differenceWith, each, every, find, filter, first, flatten, get, has, includes, isEmpty, isEqual, map, omit, reduce, sortBy, values };
const CAMPUS_4_YEARS = [CAMPUS_MANOA, CAMPUS_HILO, 'WOA'];
const PRECISION_PLACES = 12;

const WORKLOAD_STATUS_MET = 'Completed';
const WORKLOAD_STATUS_NOT_MET = 'Not Completed';
const WORKLOAD_STATUS_NA = 'Excluded due to\nFaculty Status';
const WORKLOAD_STATUS_NA_SHORT = 'N/A, Excluded';
const INVALID_CSS = 'is-invalid';
const STATUS_DRAFT = 'D';
const STATUS_FOR_REVIEW = 'R';
const STATUS_FINAL = 'F';
const LABEL_DRAFT = 'Draft';
const LABEL_FOR_REVIEW = 'For Review';
const LABEL_FINAL = 'Final and Approved';
const CRS_TYPE_INDV = ['1'];
const CRS_TYPE_CLASS = ['2'];
const VALUE_MET = 'Met';
const VALUE_NOT_MET = 'Not Met';

const SMALL_HEADER_TOP_OFFSET = 100;

/**
 * Maps true/false to Yes/No.  Incoming value is a string
 */
function booleanTransform(value) {
  switch (value) {
    case 'false':
      return 'No';
    case 'true':
      return 'Yes';
    default:
      return value;
  }
}

const AUDIT_COLUMNS = [{
  value: 'AUDIT_DATE',
  label: 'Timestamp'
}, {
  value: 'AUDIT_USERNAME',
  label: 'Username'
}, {
  value: 'RECORD_STATUS',
  label: 'Record Status',
  transform: (value) => {
    switch (value) {
      case STATUS_DRAFT:
        return LABEL_DRAFT;
      case STATUS_FOR_REVIEW:
        return LABEL_FOR_REVIEW;
      case STATUS_FINAL:
        return LABEL_FINAL;
      default:
        return value;
    }
  }
}, {
  value: 'WORKLOAD_STATUS',
  label: 'Wkload Basis Status',
  transform: (value) => {
    switch (value) {
      case VALUE_MET:
        return 'Completed';
      case VALUE_NOT_MET:
        return 'Not Completed';
      default:
        return value;
    }
  }
}, {
  value: 'JOB_TYPE',
  label: 'Job Type'
}, {
  value: 'FTE',
  label: 'FTE'
}, {
  value: 'FULL_LOAD_CREDITS',
  label: 'Expected Wkload'
}, {
  value: 'LEAVE',
  label: 'Leave',
  transform: booleanTransform
}, {
  value: 'NEWH',
  label: 'Mid-Year Hire',
  transform: booleanTransform
}, {
  value: 'NOTEM',
  label: 'No Longer Empl',
  transform: booleanTransform
}, {
  value: 'CLSY',
  label: 'Actual Class Instr'
}, {
  value: 'INDY',
  label: 'Ind Instr'
}, {
  value: 'RES',
  label: 'Research'
}, {
  value: 'REGDY',
  label: 'Assigned Professional Duties'
}, {
  value: 'SERAD',
  label: 'Administrative Service'
}, {
  value: 'SERPR',
  label: 'Professional Service'
}, {
  value: 'SERPU',
  label: 'Public Community Service'
}, {
  value: 'SEREX',
  label: 'Cooperative Extension Service'
}, {
  value: 'BUY',
  label: 'Buyout'
}, {
  value: 'REL',
  label: 'Release'
}, {
  value: 'SABY',
  label: 'Sabbatical'
}, {
  value: 'OTH',
  label: 'Other'
}];

const CODE_TE_ADMIN = 'SERAD';
const CODE_TE_PRO_ACTIVITIES = 'SERPR';
const CODE_TE_SERVICE = 'SERPU';
const CODE_TE_EXTENSION = 'SEREX';
const CODE_TE_RELEASE = 'REL';
const CODE_TE_BUYOUT = 'BUY';
const CODE_TE_SABBATICAL_YEAR = 'SAB-Y';
const CODE_TE_RESEARCH = 'RES';
const CODE_TE_REGULAR_DUTIES = 'REGDY';

export default {
  name: 'FacultyAssignmentNew',
  components: {
    Loading
  },
  props: {
    facultyId: String,
    fromHome: Boolean,
    rawFacultyData: Object
  },
  data: () => ({
    CODE_ACTUAL_CLASSROOM_INSTRUCTON: 'CLS-Y',
    CODE_INDIVIDUAL_INSTRUCTION: 'IND-Y',
    CODE_OTA: 'OTA',

    TE_CODES: {
      REGULAR_DUTIES: [CODE_TE_REGULAR_DUTIES],
      RESEARCH: [CODE_TE_RESEARCH],
      SERVICE: [CODE_TE_ADMIN, CODE_TE_PRO_ACTIVITIES, CODE_TE_SERVICE, CODE_TE_EXTENSION],
      RELEASE: [CODE_TE_RELEASE],
      BUYOUT: [CODE_TE_BUYOUT],
      SABBATICAL_YEAR: [CODE_TE_SABBATICAL_YEAR]
    },

    facultyData: {
      FTE: null,
      FULL_LOAD_CREDITS: null
    },
    isLoading: false,
    showErrorMessage: null,
    saveErrorMessage: null,
    isSaveEnabled: false,
    isSaving: false,
    showToast: false,
    // to hold the record status being selected since we're are doing logic on the record status of the object
    currentRecordStatus: STATUS_DRAFT,
    recordStatuses: [{
      value: STATUS_DRAFT,
      label: LABEL_DRAFT,
      dropdownLabel: LABEL_DRAFT,
      help: 'Setting this record to "Draft" indicates this record is still being worked on.'
    }, {
      value: STATUS_FOR_REVIEW,
      label: LABEL_FOR_REVIEW,
      dropdownLabel: `${LABEL_FOR_REVIEW} (Dept Chairs)`,
      help: 'Setting this record to "For Review" indicates this record is ready to be reviewed by a Dean/Vice Chancellor or equivalent for final approval.'
    }, {
      value: STATUS_FINAL,
      label: LABEL_FINAL,
      dropdownLabel: `${LABEL_FINAL} (Dean/VCs)`,
      help: 'Setting this record to "Final and Approved" indicates this record has been reviewed and approved for reporting purposes.'
    }],

    customLabels: {},
    showTooltips: true,
    originalNonInstructional: [],
  }),
  computed: {
    ...mapGetters('session', [
      'session',
      'sessionHasRole',
      'sessionWat',
      'sessionGetDisplayClass'
    ]),
    getBadgeClass: function () {
      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        return 'badge-secondary';
      }
      else if (this.recordIsForReview || !this.facultyData.RECORD_PHASE) {
        return 'badge-info';
      }
      else {
        return 'badge-success';
      }
    },
    hasNonInstExplain: function () {
      let contains = false;
      for(const row in this.facultyNonInstructional){
        if(this.facultyNonInstructional[row].ATTRIBUTE_EXPLAIN){
          contains = true;
          break;
        }
      }
      return contains;
    },
    badgeLabel: function () {
      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        return LABEL_DRAFT;
      }
      else if (this.recordIsForReview) {
        return LABEL_FOR_REVIEW;
      }
      else {
        return LABEL_FINAL;
      }
    },
    getCommentsClass: function () {
      if (!this.is4YearCampus && _.isEmpty(this.facultyData.COMMENTS)) {
        // comments are required for CCs and if TEs are different
        const newValues = _.concat(this.getNonTeForComparison(this.facultyData.NONINSTR), this.getNonTeForComparison(this.facultyData.INSTR));
        if (!_.isEmpty(_.differenceWith(this.originalNonInstructional, newValues, _.isEqual))) {
          return INVALID_CSS;
        }
      }
      return '';
    },
    hasEditAccess: function () {
      return _.includes(this.sessionWat.canEdit, this.facultyId) || _.includes(this.sessionWat.canFinalize, this.facultyId);
    },
    canSeeAudit: function () {
      return this.sessionHasRole(util.ROLE_ADMIN_DEBUG);
    },
    canEditWat: function () {
      if (this.sessionHasRole(util.ROLE_ADMIN_DEBUG)) {
        // if IRAPO admin, should not be affected by any record phases
        return this.hasEditAccess;
      }
      else if (!this.facultyData.RECORD_PHASE) {
        return false;
      }
      if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_FINAL || this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_PREVIEW) {
        // if final phase or preview, no can edit
        return false;
      }
      else if (this.recordIsFinal) {
        // if in final status, only if have A role on the record can edit
        return this.roleOnRecord === 'A';
      }
      else if (this.recordIsForReview && !this.canFinalizeWat) {
        // record is read only if can't finalise and it's for review
        return false;
      }
      else if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_REVIEW && this.roleOnRecord !== watUtil.ROLE_ADMIN && this.roleOnRecord !== watUtil.ROLE_REVIEWER) {
        // if in review phase, must have reviewer/admin roles to edit
        return false
      }
      else {
        return this.hasEditAccess;
      }
    },
    canFinalizeWat: function () {
      return this.sessionWat.canFinalize ? _.includes(this.sessionWat.canFinalize, this.facultyId) : false;
    },
    canSave: function () {
      return this.isSaveEnabled && !this.isSaving && this.facultyDataIsValid && this.canEditWat;
    },
    currentRecordStatusLabel: function () {
      const currentStatus = _.find(this.recordStatuses, { value: this.currentRecordStatus });
      return _.has(currentStatus, 'label') ? currentStatus.label.replace('Status: ', '') : '';
    },
    templateHelpText: function () {
      return (_.find(this.recordStatuses, { value: this.currentRecordStatus }) || {}).help;
    },
    filteredRecordStatuses: function () {
      if (this.canFinalizeWat || this.recordIsFinal) {
        return this.recordStatuses;
      }
      else {
        return _.filter(this.recordStatuses, record => record.value !== STATUS_FINAL);
      }
    },
    roleOnRecord: function () {
      return _.has(this.facultyData, 'ROLE') ? this.facultyData.ROLE : null;
    },
    recordIsForReview: function () {
      return _.has(this.facultyData, 'RECORD_STATUS') && this.facultyData.RECORD_STATUS === STATUS_FOR_REVIEW;
    },
    recordIsFinal: function () {
      return _.has(this.facultyData, 'RECORD_STATUS') && this.facultyData.RECORD_STATUS === STATUS_FINAL;
    },
    targetIsForReview: function () {
      return this.currentRecordStatus === STATUS_FOR_REVIEW;
    },
    targetIsFinal: function () {
      return this.currentRecordStatus === STATUS_FINAL;
    },
    shouldCheckValidation: function () {
      return this.currentRecordStatus !== STATUS_DRAFT;
    },
    headerDepartmentTitle: function () {
      return `${this.facultyData.CAMPUS} > ${this.facultyData.EAC_DIVISION_DESC} > ${this.facultyData.EAC_SECTION_DESC}`;
    },
    departmentTitle: function () {
      // department and job title
      let toReturn = this.facultyData.EAC_SECTION_DESC || '';
      if (!_.isEmpty(toReturn)) {
        toReturn += ',';
      }

      if (!_.isEmpty(this.facultyData.JOB_TITLE)) {
        toReturn += ` ${this.facultyData.JOB_TITLE}`;
      }
      return toReturn;
    },
    multipleAppt: function () {
      return _.has(this.facultyData, 'MULTIPLE_APPT') ? this.facultyData.MULTIPLE_APPT : null;
    },
    facultyStatuses: function () {
      return _.sortBy(this.facultyData.STATUS, status => parseInt(status.DISPLAY_ORDER));
    },
    facultySummary: function () {
      return _.sortBy(this.facultyData.SUMMARY, status => parseInt(status.DISPLAY_ORDER));
    },
    facultyNonInstructional: function () {
      return _.sortBy(this.facultyData.NONINSTR, status => parseInt(status.DISPLAY_ORDER));
    },
    individualInstructionClasses: function () {
      if (_.has(this.facultyData, 'CLASSES')) {
        const classes = _.filter(this.facultyData.CLASSES, classObj => _.includes(CRS_TYPE_INDV, classObj.CRS_TYPE));
        return _.sortBy(_.map(classes, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
      }
      return [];
    },
    classRoomInstructionClasses: function () {
      if (_.has(this.facultyData, 'CLASSES')) {
        const classes = _.filter(this.facultyData.CLASSES, classObj => _.includes(CRS_TYPE_CLASS, classObj.CRS_TYPE));
        return _.sortBy(_.map(classes, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
      }
      return [];
    },
    combinedCCClasses: function () {
      return _.sortBy(_.map(this.facultyData.CLASSES, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
    },
    is4YearCampus: function () {
      return _.includes(CAMPUS_4_YEARS, this.facultyData.CAMPUS);
    },
    facultyDataIsValid: function () {
      let isValid = true;

      // comments class should always be checked since if you can without comments in draft, we can't be sure what the original values were
      isValid = _.isEmpty(this.getCommentsClass);

      // short circuit out if no need to check
      if (!this.shouldCheckValidation) {
        return isValid;
      }

      // check numbers entered are positive
      if (isValid) {
        isValid = _.every(this.facultyData.INSTR, (row) => {
          // if not a number type or it's empty, it's valid
          if (row.ATTRIBUTE_FORMAT !== 'N' || _.isEmpty(row.ATTRIBUTE_VALUE)) {
            return true;
          }
          return parseFloat(row.ATTRIBUTE_VALUE) >= 0;
        });
      }
      return isValid;
    },
    computedNonInstructionalTE: function () {
      const total = _.reduce(this.facultyData.NONINSTR, (sum, row) => {
        return sum + parseFloat(row.ATTRIBUTE_VALUE || 0);
      }, 0);
      return parseFloat(total.toPrecision(PRECISION_PLACES));
    },
    computedInstructionalTE: function () {
      return parseFloat((this.computedIndividualTE + this.computedClassroomTE).toPrecision(PRECISION_PLACES));
    },
    computedIndividualTE: function () {
      const total = parseFloat(this.getFacultyInstructionalForCode(this.CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE || 0);
      return parseFloat((total).toPrecision(PRECISION_PLACES));
    },
    computedClassroomTE: function () {
      const total = parseFloat(this.getFacultyInstructionalForCode(this.CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE || 0);
      return parseFloat((total).toPrecision(PRECISION_PLACES));
    },
    expectedLoad: function () {
      return parseFloat((parseFloat(this.facultyData.FTE || 0) * parseFloat(this.facultyData.FULL_LOAD_CREDITS || 0)).toPrecision(PRECISION_PLACES));
    },
    hasAnyFacultyStatus: function () {
      return _.find(this.facultyStatuses, { ATTRIBUTE_VALUE: true });
    },
    hasMetWorkloadStatus: function () {
      // if any status is checked, it's N/A
      if (this.hasAnyFacultyStatus) {
        return WORKLOAD_STATUS_NA;
      }

      // they are eligible to be met or not met so calculate
      return this.actualLoad >= this.expectedLoad ? WORKLOAD_STATUS_MET : WORKLOAD_STATUS_NOT_MET;
    },
    workloadStatusClass: function () {
      if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET) {
        return 'text-success';
      }
      else if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET) {
        return 'text-warning';
      }
      else {
        return 'text-info';
      }
    },
    workloadStatusIcon: function () {
      if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET) {
        return ['far', 'check-circle'];
      }
      else if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET) {
        return ['fas', 'exclamation-triangle'];
      }
      else {
        return ['fas', 'info-circle'];
      }
    },
    getAcademicYearStart: function () {
      // converts the reporting year of the record into the academic year string
      if (_.has(this.facultyData, 'REPORTING_YEAR')) {
        const previousYear = parseFloat(this.facultyData.REPORTING_YEAR) - 1;
        return `${previousYear}`;
      }
      else {
        return '';
      }
    },
    getAcademicYearEnd: function () {
      return this.facultyData.REPORTING_YEAR;
    },
    toastPosition: function () {
      // if we're not showing the toast,  make it off screen so it doesnt block the save button
      return `position: fixed; top: ${this.showToast ? '40px' : '-100px'}; right: 1rem; z-index: 1050;`;
    },
    jobTypes: function () {
      return watUtil.WAT_JOB_TYPES;
    },
    equivalencyLabel: function () {
      return this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL ? 'Teaching' : 'Workload';
    },
    auditColumns: function () {
      const AUDIT_FIELDS_REQUIRED = ['AUDIT_DATE', 'AUDIT_USERNAME'];
      const auditEntries = this.facultyData.fullAudit || [];
      const changedFields = _.flatten(_.map(auditEntries, entry => entry.changed || []));

      // if it's a required field we're showing or there's a change to the field
      return _.filter(AUDIT_COLUMNS, field => _.includes(AUDIT_FIELDS_REQUIRED, field.value) || _.includes(changedFields, field.value));
    },

    researchTeSection: function () {
      const results = _.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.RESEARCH, codeType.ATTRIBUTE_CODE));
      return results.length > 0 ? results[0] : {};
    },
    regularDutiesTeSection: function () {
      const results = _.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.REGULAR_DUTIES, codeType.ATTRIBUTE_CODE));
      return results.length > 0 ? results[0] : {};
    },
    serviceTeSection: function () {
      return _.sortBy(_.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.SERVICE, codeType.ATTRIBUTE_CODE)), 'DISPLAY_ORDER') || [];
    },
    releaseTeSection: function () {
      const results = _.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.RELEASE, codeType.ATTRIBUTE_CODE));
      return results.length > 0 ? results[0] : {};
    },
    buyoutTeSection: function () {
      const results = _.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.BUYOUT, codeType.ATTRIBUTE_CODE));
      return results.length > 0 ? results[0] : {};
    },
    sabbaticalTeSection: function () {
      const results = _.filter(this.facultyNonInstructional, codeType => _.includes(this.TE_CODES.SABBATICAL_YEAR, codeType.ATTRIBUTE_CODE));
      return results.length > 0 ? results[0] : {};
    },
    actualLoad: function () {
      return parseFloat((_.reduce(_.filter(this.facultyNonInstructional, codeType => _.includes(_.flatten(_.values(this.TE_CODES)), codeType.ATTRIBUTE_CODE)), (sum, row) => {
        return sum + parseFloat(row.ATTRIBUTE_VALUE || 0);
      }, 0) + this.computedIndividualTE + this.computedClassroomTE).toPrecision(PRECISION_PLACES));
    },
    creditsLabel: function () {
      if (this.hasAnyFacultyStatus) {
        return 'N/A';
      }
      return `${this.actualLoad} out of ${this.expectedLoad} expected workload credits`;
    },
    creditsLabelForSticky: function () {
      if (this.hasAnyFacultyStatus) {
        return WORKLOAD_STATUS_NA_SHORT;
      }
      return `${this.actualLoad} out of ${this.expectedLoad} expected workload credits`;
    },
    getStatusLabel: function () {
      let message = '';
      let phaseMessage = '';

      if (!this.facultyData.RECORD_PHASE) {
        return 'This record is only available to IRAPO.';
      }

      if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_PREVIEW) {
        const dateObject = DateTime.fromISO(watUtil.RECORD_RELEASE_DATE);
        return `This record is in a pre-release preview.  It will be available for editing on ${dateObject.toLocaleString(DateTime.DATE_MED)}`;
      }
      else if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_FINAL) {
        phaseMessage = '.  [Data collection closed for ALL users]';
      }
      else if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_REVIEW) {
        phaseMessage = '.  [Data collection closed for general users]';
      }

      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        message = "This record is still in progress (DRAFT)";
      }
      else if (this.recordIsForReview) {
        message = "This record is ready for review (FOR REVIEW)";
        if (!this.canFinalizeWat && this.hasEditAccess) {
          message = `${message}.  If you need to make changes, please contact your Dean/Vice Chancellor.`;
          phaseMessage = '';
        }
      }
      else if (this.facultyData.RECORD_STATUS === STATUS_FINAL) {
        message = "This record has been finalized and approved (FINAL AND APPROVED)";
      }
      return `${message}${phaseMessage}`;
    },
    individualShouldBeDisabled: function () {
      return this.facultyData.CAMPUS === CAMPUS_HILO;
    },
    regularDutiesShouldBeDisabled: function () {
      return this.teShouldBeDisabled(CODE_TE_REGULAR_DUTIES);
    },
    releaseShouldBeDisabled: function () {
      return this.teShouldBeDisabled(CODE_TE_RELEASE);
    },
    buyoutShouldBeDisabled: function () {
      return this.teShouldBeDisabled(CODE_TE_BUYOUT);
    },
  },
  methods: {
    makeDirty: function () {
      this.isSaveEnabled = true;
      this.$emit('make-dirty', true);
    },
    async init() {
      this.isLoading = true;
      try {
        const facultyDataResponse = _.cloneDeep(this.rawFacultyData);

        // convert the status string bools to booleans
        facultyDataResponse.STATUS = _.map(facultyDataResponse.STATUS, (status) => {
          if (_.has(status, 'ATTRIBUTE_VALUE')) {
            status.ATTRIBUTE_VALUE = status.ATTRIBUTE_VALUE === 'true';
          }
          return status;
        });

        // make a copy of the original non-instructional TE for comparison; for Hilo, instructional and non-instructional require
        this.originalNonInstructional = _.concat(this.getNonTeForComparison(facultyDataResponse.NONINSTR), this.getNonTeForComparison(facultyDataResponse.INSTR));

        // load the custom tool tips
        await this.loadCustomTooltips(facultyDataResponse.CAMPUS);

        // run any audit entry translations
        const hasTransforms = _.filter(AUDIT_COLUMNS, column => column.transform);
        _.each(hasTransforms, (auditCol) => {
          _.each(facultyDataResponse.audit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });

          _.each(facultyDataResponse.fullAudit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });
        });

        this.facultyData = facultyDataResponse;

        // update session template roles in the session
        this.$store.dispatch('session/setSessionWatTemplateRoles', [{
          id: this.facultyData.FACULTY_RECORD_UID,
          role: this.facultyData.ROLE,
        }]);

        // make sure to copy in so we can update it
        this.currentRecordStatus = this.facultyData.RECORD_STATUS;
      }
      catch (e) {
        console.error(e);
        if (_.has(e, 'body.message')) {
          this.showErrorMessage = e.body.message;
        }
        else {
          this.showErrorMessage = 'Whoops, there was an error loading the content.';
        }
      }
      finally {
        this.isLoading = false;
      }
    },
    loadCustomTooltips: async function (campus) {
      try {
        this.customLabels = await assets.get(`json/wat/tooltips/${_.includes(CAMPUS_4_YEARS, campus) ? campus : "UHCC"}.json`);
      }
      catch (_err) {
        this.customLabels = {};
      }
    },
    doSaveAsDraft() {
      return this.doSave(STATUS_DRAFT);
    },
    async doSave(recordStatusOverride) {

      // show the save confirmation if we're not overriding and not draft
      if (this.currentRecordStatus !== STATUS_DRAFT && !recordStatusOverride) {
        $('#modalSaveConfirm').modal();
        return;
      }

      // set the record status to the override if it's there
      if (recordStatusOverride) {
        this.currentRecordStatus = recordStatusOverride;
      }

      this.isSaving = true;
      this.showToast = true;
      this.saveErrorMessage = null;
      try {
        // set the computed noninstructional TE (must be a string)
        const nonInstructional = _.find(this.facultyData.SUMMARY, { ATTRIBUTE_CODE: 'NONIN' });
        if (nonInstructional) {
          nonInstructional.ATTRIBUTE_VALUE = `${this.computedNonInstructionalTE}`;
        }

        // set the computed instructional TE adjustments
        const fallInstructionalAdjustment = this.getFacultyInstructionalForCode('CLSFA');
        fallInstructionalAdjustment.ATTRIBUTE_VALUE = `${this.getClassroomTEDifference("F")}`;
        const springInstructionalAdjustment = this.getFacultyInstructionalForCode('CLSSA');
        springInstructionalAdjustment.ATTRIBUTE_VALUE = `${this.getClassroomTEDifference("S")}`;

        // also copy the updated record status, we'll clone it so we don't mutate our existing
        // in the event of an error
        const dataToSave = _.cloneDeep(_.omit(this.facultyData, ['audit', 'fullAudit']));
        dataToSave.RECORD_STATUS = this.currentRecordStatus;

        // do the save
        this.facultyData = await watResource.put(`api/w/facstaff/wat/${this.facultyId}`, dataToSave, null, this.$route.query);

        // convert the status string bools to booleans
        this.facultyData.STATUS = _.map(this.facultyData.STATUS, (status) => {
          status.ATTRIBUTE_VALUE = status.ATTRIBUTE_VALUE === 'true';
          return status;
        });

        // make a copy of the original non-instructional TE for comparison
        this.originalNonInstructional = _.concat(this.getNonTeForComparison(this.facultyData.NONINSTR, this.getNonTeForComparison(this.facultyData.INSTR)));

        // run any audit entry translations
        const hasTransforms = _.filter(AUDIT_COLUMNS, column => column.transform);
        _.each(hasTransforms, (auditCol) => {
          _.each(this.facultyData.audit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });

          _.each(this.facultyData.fullAudit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });
        });


        this.isSaveEnabled = false;
        $('.success-toast').toast('show');
        this.$emit('make-dirty', false);

        // fire the event to notify the list page
        eventBus.bus.$emit(eventBus.CHANNEL_WAT_RECORD_UPDATED, {
          FACULTY_RECORD_UID: this.facultyData.FACULTY_RECORD_UID,
          RECORD_STATUS: this.facultyData.RECORD_STATUS,
          WORKLOAD_STATUS: this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET ? 'Met' : this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET ? 'Not Met' : 'N/A',
          BASIS: this.expectedLoad,
          WAU: this.actualLoad
        });
      }
      catch (saveErr) {
        console.error(saveErr);
        if (_.has(saveErr, 'body.message')) {
          this.saveErrorMessage = saveErr.body.message;
        }
        $('.error-toast').toast('show');
      }
      finally {
        this.isSaving = false;
      }
    },
    onJobTypeChange(facultyObj) {
      facultyObj.FULL_LOAD_CREDITS = watUtil.getExpectedWorkload(facultyObj.CAMPUS, facultyObj.JOB_TYPE, facultyObj.JOB_CODE);

      // clear out any Regular Duties if changing to instructional
      if (facultyObj.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL) {
        const regularDuties = _.find(facultyObj.NONINSTR, { ATTRIBUTE_CODE: CODE_TE_REGULAR_DUTIES });
        if (regularDuties) {
          regularDuties.ATTRIBUTE_VALUE = '0';
        }
      }

      this.makeDirty();
    },
    getClassroomTEDifference(semester) {
      const code = semester === 'F' ? 'CLS-F' : 'CLS-S';
      const estimatedCode = semester === 'F' ? 'CLSFE' : 'CLSSE';
      const value = this.getFacultyInstructionalForCode(code);
      const estimatedValue = this.getFacultyInstructionalForCode(estimatedCode);
      const diff = parseFloat(value.ATTRIBUTE_VALUE || 0) - parseFloat(estimatedValue.ATTRIBUTE_VALUE || 0);
      return isNaN(diff) ? 0 : diff;
    },
    getNegativeAndRequiredValidation(value) {
      let toReturn = this.getNegativeNumberValidation(value);
      if (_.isEmpty(toReturn)) {
        toReturn = this.getRequiredValidation(value);
      }
      return toReturn;
    },
    getNegativeNumberValidation(value) {
      let toReturn = '';
      if (this.shouldCheckValidation) {
        const val = parseFloat(value);

        // if there is a value and it's not a number or less than 0, it's invalid
        if (!_.isEmpty(value) && (isNaN(val) || val < 0)) {
          toReturn = INVALID_CSS;
        }
      }
      return toReturn;
    },
    getRequiredValidation(value) {
      return _.isEmpty(value) ? INVALID_CSS : '';
    },
    getFacultyInstructionalForCode(code) {
      return _.find(this.facultyData.INSTR, { ATTRIBUTE_CODE: code }) || {};
    },
    roundValue(type, code, places) {
      const placesToUse = places || 2;
      const obj = _.find(this.facultyData[type], { ATTRIBUTE_CODE: code }) || {};
      const numberValue = parseFloat(obj.ATTRIBUTE_VALUE);
      if (!isNaN(numberValue)) {
        obj.ATTRIBUTE_VALUE = parseFloat(numberValue.toFixed(placesToUse)).toString();
      }
    },
    roundTopLevelValue(type, places) {
      const placesToUse = places || 2;
      const numberValue = parseFloat(this.facultyData[type]);
      if (!isNaN(numberValue)) {
        this.facultyData[type] = parseFloat(numberValue.toFixed(placesToUse)).toString();
        this.makeDirty();
      }
    },
    getRouterLink(name) {
      return {
        name,
        query: this.$route.query
      }
    },
    parseClassroomObject(classRecord) {
      const toReturn = classRecord;
      let termDesc = "";
      if(classRecord.SEM_YR_IRO.indexOf('-8') >= 0) {
        termDesc = 'Fall' + " " + classRecord.SEM_YR_IRO.substring(0, 4);
      }
      else if(classRecord.SEM_YR_IRO.indexOf('-1') >= 0) {
        termDesc = 'Spring' + " " + classRecord.SEM_YR_IRO.substring(0, 4);
      }
      else {
        termDesc= 'Summer' + " " + classRecord.SEM_YR_IRO.substring(0, 4);
      }
      toReturn.TERM = termDesc;
      return toReturn;
    },
    scrollToTop: function () {
      $('html,body').scrollTop(0);
    },
    onScroll: function () {
      const SHADOW_CLASS = 'shadow';
      const stickyHeader = document.getElementById('the-sticky-header');
      if (stickyHeader) {
        if (window.pageYOffset > SMALL_HEADER_TOP_OFFSET) {
          stickyHeader.classList.add('show-small');
          stickyHeader.classList.add(SHADOW_CLASS);
        }
        else {
          stickyHeader.classList.remove('show-small');
          stickyHeader.classList.remove(SHADOW_CLASS);
        }

        const isNavHidden = document.getElementsByClassName('hideable-header-hidden').length > 0;
        if (isNavHidden) {
          stickyHeader.classList.add('nav-hidden');
        }
        else {
          stickyHeader.classList.remove('nav-hidden');
        }
      }
    },
    showAuditHistory: function () {
      $('#modalAudit').modal();
    },
    includes: function (values, value) {
      return _.includes(values, value);
    },
    updateStickyHeader: function () {
      // update the padding left/right of the sticky header
      if (this.$refs.pageBody) {
        const { left } = this.$refs.pageBody.getBoundingClientRect();
        const stickyHeader = document.getElementById('the-sticky-header');
        if (stickyHeader) {
          stickyHeader.style.paddingLeft = `${left}px`;
          stickyHeader.style.paddingRight = `${left}px`;
        }
      }
    },
    getCustomLabel: function (codePath, defaultText) {
      if (_.has(this.customLabels, codePath)) {
        return _.get(this.customLabels, codePath);
      }
      return defaultText;
    },
    teShouldBeDisabled: function (code) {
      if (code === CODE_TE_RELEASE) {
        return !this.is4YearCampus || this.facultyData.CAMPUS === CAMPUS_MANOA || this.facultyData.CAMPUS === CAMPUS_HILO;
      }
      else if (code === CODE_TE_BUYOUT) {
        return this.facultyData.CAMPUS === CAMPUS_MANOA || this.facultyData.CAMPUS === CAMPUS_HILO;
      }
      else if (code === CODE_TE_EXTENSION) {
        // disabled for everyone
        return true;
      }
      else if (code === CODE_TE_REGULAR_DUTIES) {
        return this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL
      }

      // default not disabled
      return false;
    },
    getNonTeForComparison: function (values) {
      return _.map(values, row => ({
        ATTRIBUTE_CODE: row.ATTRIBUTE_CODE,
        ATTRIBUTE_VALUE: row.ATTRIBUTE_VALUE || ''
      }));
    },
  },
  mounted: function () {
    $('.success-toast').on('hidden.bs.toast', () => {
      this.showToast = false;
    });
    $('.error-toast').on('hidden.bs.toast', () => {
      this.showToast = false;
    });

    window.addEventListener('scroll', this.onScroll);
    window.addEventListener('resize', this.updateStickyHeader);

    // hide the support button
    eventBus.bus.$emit(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, {
      visible: false
    });
  },
  created: function () {
    this.init();
  },
  updated: function () {
    $(() => {
      $('[data-toggle="tooltip"]').tooltip()
    });

    this.updateStickyHeader();
  },
  beforeDestroy: function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateStickyHeader);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
  @import '../../assets/app.styl'

  .header-left-box
    width 120px
  .header-right-box
    width 300px

  .faculty-header
    background-color #ccdaf5

  .header-assigned-workload
    background-color #fdf2d0

  .article
    display flex
  .article .item, .article .price
    flex 1 0 auto

  .article .dots
   flex: 0 1 auto
   overflow: hidden

  .dots::before
    display block
    white-space nowrap
    overflow hidden
    text-overflow clip
    margin 0 5px
    content ". . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . "

  .shorter-input
    height 1.75em
    width 100px

  .hoverable-row
    &:hover
      background-color datarow-highlight-color

  .bg
    background-color white

  .bg-dark
    background-color dark-mode-background-color

  .full-width
    width 100%

  .extra-padding
    padding-top 50px

  .sticky-top
    top 70px !important
    transition 0.2s all ease-out
    z-index auto

  .sticky-top.has-header
    top 150px !important
    transition 0.2s all ease-out

  .form-check
    input, label
      cursor pointer

  .sticky-header
    padding-top 90px
    padding-left 1rem
    padding-right 1rem
    background white
    z-index 99
    position fixed
    top 0px
    left 0px
    width 100%

  .sticky-header-dark
    background dark-background-color !important

  .section
    background white
    border-radius 1rem
    padding-left 15px
    padding-right 15px

  .section-dark
    background-color dark-mode-background-color

  .help-icon
    font-size .9rem


  .show-small
    transform translate3d(0, 0, 0)
    transition 0.2s all ease-out

    .record-status
      position absolute
      height 0 !important
      transform translate3d(0, -300%, 0)
      transition 0.2s all ease-out
    .small-header-name
      display block !important
      transition 0.2s all ease-out

  .nav-hidden
    padding-top 10px !important
    transition 0.2s all ease-out

  .collapsed.when-opened
    display none
  :not(.collapsed).when-closed
    display none

  .faculty-status
    border 1px solid #ced4da
    border-radius .25rem

  .smaller-text
    font-size .75rem

  .modal-wide
    max-width 90%

  .show-tooltips-button
    position fixed
    right 0
    bottom 0
    z-index 1030
    transition all .5s ease
    color white

  @media (prefers-color-scheme: dark)
    .sticky-header-system
      background dark-background-color !important
</style>
import { includes } from 'lodash';

